import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectPortalSettings,
  selectPortalSettingsError,
  selectPortalSettingsStatus,
} from "../../features/portal-settings/portal-settings-slice";
import {
  fetchPortalSettings,
  updatePortalSettings,
} from "../../features/portal-settings/slice-extensions/portal-settings-slice-extension";
import { UpdatePortalSettingsRequest } from "../../features/portal-settings/contracts/UpdatePortalSettingsRequest";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import SessionRemindersSettings from "./SessionRemindersSettings";

type SwitchKey = Exclude<keyof UpdatePortalSettingsRequest, "sessionRemindersEnabled" | "sessionReminderLeadHours">;

interface PortalFeaturesCardProps {
  /** An Instructor-role user sees the settings but cannot change them. */
  readOnly?: boolean;
}

/**
 * Per-organization switches for what members see in the portal: absence
 * self-service, the shared absence calendar, materials, and member uploads.
 * Everything defaults to off. After the trial the absence switches need the
 * Club plan; the materials switches work on every plan.
 * Below them, the session reminders (on by default at 24 hours).
 */
const PortalFeaturesCard = ({ readOnly = false }: PortalFeaturesCardProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectPortalSettings);
  const status = useAppSelector(selectPortalSettingsStatus);
  const error = useAppSelector(selectPortalSettingsError);

  useEffect(() => {
    dispatch(fetchPortalSettings());
  }, [dispatch]);

  const save = (changes: Partial<UpdatePortalSettingsRequest>) => {
    if (!settings) return;
    // Round-trip the full state so one switch never resets another.
    dispatch(
      updatePortalSettings({
        memberAbsencesEnabled: settings.memberAbsencesEnabled,
        absenceCalendarEnabled: settings.absenceCalendarEnabled,
        materialsEnabled: settings.materialsEnabled,
        memberUploadsEnabled: settings.memberUploadsEnabled,
        sessionRemindersEnabled: settings.sessionRemindersEnabled,
        sessionReminderLeadHours: settings.sessionReminderLeadHours,
        ...changes,
      }),
    );
  };

  const handleToggle = (key: SwitchKey) => {
    if (!settings) return;
    save({ [key]: !settings[key] });
  };

  const busy = !settings || status === LoadingStatus.Loading || readOnly;

  const switches: {
    key: SwitchKey;
    label: string;
    hint: string;
    dependsOn?: SwitchKey;
    planNote?: string;
  }[] = [
    {
      key: "memberAbsencesEnabled",
      label: t("website.portalFeatures.absences", "Members can record absences"),
      hint: t("website.portalFeatures.absencesHint", "Members enter their own vacations in the portal."),
    },
    {
      key: "absenceCalendarEnabled",
      label: t("website.portalFeatures.absenceCalendar", "Shared absence calendar"),
      hint: t("website.portalFeatures.absenceCalendarHint", "Members see who is away when (first names only)."),
      dependsOn: "memberAbsencesEnabled",
      // Only the absence switches need the Club plan after the trial; materials are on every plan.
      planNote: t(
        "website.portalFeatures.planHint",
        "Absences and the shared absence calendar are included in the free trial and the Club plan.",
      ),
    },
    {
      key: "materialsEnabled",
      label: t("website.portalFeatures.materials", "Show materials"),
      hint: t("website.portalFeatures.materialsHint", "Members can view and download your shared files."),
    },
    {
      key: "memberUploadsEnabled",
      label: t("website.portalFeatures.memberUploads", "Members can upload materials"),
      hint: t("website.portalFeatures.memberUploadsHint", "Members add files too, and can delete their own uploads."),
      dependsOn: "materialsEnabled",
    },
  ];

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <h2 className="text-lg font-semibold text-grey-20 dark:text-white">
        {t("website.portalFeatures.title", "Member portal features")}
      </h2>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t("website.portalFeatures.intro", "Choose what members can see and do in their portal. Everything is off until you switch it on.")}
      </p>

      <div className="mt-4 space-y-3">
        {switches.map(({ key, label, hint, dependsOn, planNote }) => {
          const parentOff = dependsOn !== undefined && settings?.[dependsOn] === false;
          return (
            <Fragment key={key}>
              <label
                className={`flex cursor-pointer items-start gap-3 ${dependsOn ? "ml-6" : ""}`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={settings?.[key] ?? false}
                  onChange={() => handleToggle(key)}
                  disabled={busy || parentOff}
                />
                <span>
                  <span className="block text-sm font-medium text-grey-20 dark:text-white">
                    {label}
                  </span>
                  <span className="block text-xs text-grey-40 dark:text-grey-80">{hint}</span>
                </span>
              </label>
              {planNote && (
                <p className="ml-6 text-xs text-grey-50 dark:text-grey-70">{planNote}</p>
              )}
            </Fragment>
          );
        })}
      </div>

      <SessionRemindersSettings
        enabled={settings?.sessionRemindersEnabled ?? true}
        leadHours={settings?.sessionReminderLeadHours ?? 24}
        disabled={busy}
        onChange={(enabled, leadHours) =>
          save({ sessionRemindersEnabled: enabled, sessionReminderLeadHours: leadHours })
        }
      />

      {readOnly && (
        <p className="mt-3 text-xs text-grey-50 dark:text-grey-70">
          {t("website.portalFeatures.readOnlyHint", "Only owners and admins can change these settings.")}
        </p>
      )}

      {error && <p className="field-error mt-2">{error.message}</p>}
    </div>
  );
};

export default PortalFeaturesCard;
